// @refresh reset
import { createContext, useContext, useState, useCallback, useEffect, type ReactNode } from "react";
import { useClientReady } from '@/lib/initialRender';

export interface FavoriteProduct {
  handle: string;
  title: string;
  image: string | null;
  price: string;
}

interface FavoritesContextValue {
  favorites: FavoriteProduct[];
  isFavorite: (handle: string) => boolean;
  toggleFavorite: (product: FavoriteProduct) => void;
}

const FavoritesContext = createContext<FavoritesContextValue | null>(null);

const STORAGE_KEY = "bkt_favorites";

export function FavoritesProvider({ children }: { children: ReactNode }) {
  const clientReady = useClientReady();
  const [favorites, setFavorites] = useState<FavoriteProduct[]>(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      return saved ? (JSON.parse(saved) as FavoriteProduct[]) : [];
    } catch {
      return [];
    }
  });

  useEffect(() => {
    // Synchroniczny rzut w ciele useEffect trafia prosto do najbliższego
    // ErrorBoundary — tryb prywatny Safari / pełny storage potrafi rzucić
    // tu przy KAŻDEJ zmianie ulubionych, więc to musi być zabezpieczone.
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(favorites));
    } catch {
      // storage niedostępny — ulubione działają dalej w pamięci tej sesji
    }
  }, [favorites]);

  const isFavorite = useCallback(
    (handle: string) => clientReady && favorites.some((f) => f.handle === handle),
    [favorites, clientReady],
  );

  const toggleFavorite = useCallback((product: FavoriteProduct) => {
    setFavorites((prev) => {
      const exists = prev.some((f) => f.handle === product.handle);
      return exists
        ? prev.filter((f) => f.handle !== product.handle)
        : [...prev, product];
    });
  }, []);

  return (
    <FavoritesContext.Provider value={{ favorites: clientReady ? favorites : [], isFavorite, toggleFavorite }}>
      {children}
    </FavoritesContext.Provider>
  );
}

export function useFavorites() {
  const ctx = useContext(FavoritesContext);
  if (!ctx) throw new Error("useFavorites must be used inside FavoritesProvider");
  return ctx;
}
